import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    Row,
    Col,
    Card,
    Table,
    Select,
    DatePicker,
    Space,
    Progress,
    List,
    Avatar
} from 'antd';
import { Animated } from "react-animated-css";
import { Line as LinePlot, Pie as PiePlot } from '@ant-design/plots';
import {
    EyeOutlined,
    ImportOutlined,
    PercentageOutlined,
    GlobalOutlined,
    UserOutlined,
    RiseOutlined
} from '@ant-design/icons';
import { businessUnit } from '../../config';

import './Dashboard.scss';

import { adeventList } from '../../modules/adevent';

const { RangePicker } = DatePicker;

class Dashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadingAnalytics: false,
            loadingMetric: false,
            loadingGeo: false,
            loadingAdFormat: false,
            selectedRegion: 'all',
            selectedAdFormat: 'all',
            selectedBusinessUnit: null,
            selectedMetric: 'impressions',
            dateRange: null,
            rawRows: [],
            dashboardAnalytics: null,
            dashboardMetricData: null,
            dashboardGeoData: null,
            dashboardAdFormatData: null,
            recentActivities: [
                {
                    id: 1,
                    title: 'New Campaign Launch: Mobile Video Ads',
                    time: '2 hours ago',
                    user: 'Marketing Team',
                    type: 'campaign'
                },
                {
                    id: 2,
                    title: 'Delivery Milestone: Ad Return Target Reached',
                    time: '5 hours ago',
                    user: 'Sales Team',
                    type: 'delivery'
                },
                {
                    id: 3,
                    title: 'New Region Added: United Kingdom',
                    time: '1 day ago',
                    user: 'Operations Team',
                    type: 'expansion'
                },
                {
                    id: 4,
                    title: 'Performance Optimization: CTR Improved by 15%',
                    time: '2 days ago',
                    user: 'Tech Team',
                    type: 'optimization'
                }
            ]
        }
    }

    componentDidMount() {
        this.loadAdeventData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedOrganization !== this.props.selectedOrganization) {
            this.loadAdeventData();
        }
    }

    loadAdeventData = async () => {
        this.setState({
            loadingAnalytics: true,
            loadingMetric: true,
            loadingGeo: true,
            loadingAdFormat: true,
        });

        try {
            const payload = this.buildAdeventPayload();
            const response = await this.props.adeventList(payload);
            const rows = this.normalizeAdeventResults(response?.results || []);
            this.setState({ rawRows: rows }, this.computeAll);
        } catch (error) {
            console.error('Failed loading Advert Events', error);
            this.setState({
                loadingAnalytics: false,
                loadingMetric: false,
                loadingGeo: false,
                loadingAdFormat: false,
            });
        }
    }

    buildAdeventPayload = () => {
        const { dateRange, selectedBusinessUnit } = this.state;
        const isSuperAdmin = this.isSuperAdmin();
        const payload = {
            showMore: isSuperAdmin,
        };

        if (selectedBusinessUnit) {
            payload.businessUnit = selectedBusinessUnit;
        }

        if (Array.isArray(dateRange)) {
            const [start, end] = dateRange;
            const startDate = this.formatDateForQuery(start);
            const endDate = this.formatDateForQuery(end);
            if (startDate) payload.startDate = startDate;
            if (endDate) payload.endDate = endDate;
        }

        return Object.keys(payload).reduce((acc, key) => {
            const value = payload[key];
            if (value !== undefined && value !== null && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {});
    }

    formatDateForQuery = (value) => {
        if (!value) return undefined;
        if (typeof value.format === 'function') {
            return value.format('YYYY-MM-DD');
        }
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return undefined;
        }
        return date.toISOString().slice(0, 10);
    }

    normalizeAdeventResults = (results = []) => {
        if (!Array.isArray(results)) {
            return [];
        }
        return results.map(item => this.normalizeAdeventRow(item));
    }

    normalizeAdeventRow = (row = {}) => ({
        ...row,
        impressions: this.parseMetricValue(row.impressions),
        adRequest: this.parseMetricValue(row.adRequest),
        adReturn: this.parseMetricValue(row.adReturn),
        clicks: this.parseMetricValue(row.clicks),
        ecpm: this.parseMetricValue(row.ecpm),
        ctr: this.parseMetricValue(row.ctr),
        cpc: this.parseMetricValue(row.cpc),
    });

    parseMetricValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return 0;
        }
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }
        if (typeof value === 'string') {
            const sanitized = value.replace(/[^\d.-]/g, '').trim();
            if (sanitized === '') {
                return 0;
            }
            const parsed = Number(sanitized);
            return Number.isFinite(parsed) ? parsed : 0;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    parseDateValue = (value) => {
        if (!value) {
            return null;
        }
        if (typeof value.toDate === 'function') {
            const date = value.toDate();
            return Number.isNaN(date.getTime()) ? null : date;
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    getDayBoundaryDate = (value, boundary) => {
        if (!value) {
            return null;
        }
        if (typeof value.clone === 'function' && typeof value[boundary] === 'function') {
            return this.parseDateValue(value.clone()[boundary]('day'));
        }
        if (typeof value[boundary] === 'function') {
            return this.parseDateValue(value[boundary]('day'));
        }

        const date = this.parseDateValue(value);
        if (!date) {
            return null;
        }
        const hours = boundary === 'endOf' ? 23 : 0;
        const minutes = boundary === 'endOf' ? 59 : 0;
        const seconds = boundary === 'endOf' ? 59 : 0;
        const milliseconds = boundary === 'endOf' ? 999 : 0;
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, seconds, milliseconds);
    }

    formatDateKey = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    normalizeDateKey = (value) => {
        const date = this.parseDateValue(value);
        return date ? this.formatDateKey(date) : null;
    }

    buildContinuousMetricData = (byDateMap, rows) => {
        const { dateRange } = this.state;
        let startDate = null;
        let endDate = null;

        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
            startDate = this.getDayBoundaryDate(dateRange[0], 'startOf');
            endDate = this.getDayBoundaryDate(dateRange[1], 'endOf');
        } else {
            rows.forEach(r => {
                const date = this.parseDateValue(r.date);
                if (!date) return;
                if (!startDate || date < startDate) startDate = date;
                if (!endDate || date > endDate) endDate = date;
            });
        }

        if (!startDate || !endDate) {
            return [];
        }

        const result = [];
        const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const last = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        while (cursor <= last) {
            const key = this.formatDateKey(cursor);
            result.push(byDateMap.get(key) || this.createMetricBucket({ date: key }));
            cursor.setDate(cursor.getDate() + 1);
        }

        return result;
    }

    getMetricOptions = () => ([
        { key: 'adRequest', label: 'Ad Request', shortLabel: 'Ad Request', suffix: '', precision: 0, color: '#1890ff' },
        { key: 'adReturn', label: 'Ad Return', shortLabel: 'Ad Return', suffix: '', precision: 0, color: '#13c2c2' },
        { key: 'adReturnRate', label: 'Ad Return Rate', shortLabel: 'Ad Return Rate', suffix: '%', precision: 2, color: '#52c41a' },
        { key: 'adFillRate', label: 'Ad Fill Rate', shortLabel: 'Ad Fill Rate', suffix: '%', precision: 2, color: '#2f54eb' },
        { key: 'impressions', label: 'IMP', shortLabel: 'IMP', suffix: '', precision: 0, color: '#722ed1' },
        { key: 'clicks', label: 'CLICK', shortLabel: 'CLICK', suffix: '', precision: 0, color: '#fa8c16' },
        { key: 'ctr', label: 'CTR', shortLabel: 'CTR', suffix: '%', precision: 2, color: '#eb2f96' },
    ])

    getMetricConfig = (metricKey) => (
        this.getMetricOptions().find(option => option.key === metricKey) || this.getMetricOptions()[0]
    )

    createMetricBucket = (base = {}) => ({
        ...base,
        adRequest: 0,
        adReturn: 0,
        impressions: 0,
        clicks: 0,
        adReturnRate: 0,
        adFillRate: 0,
        ctr: 0,
        value: 0,
    })

    addRowToMetricBucket = (bucket, row) => {
        bucket.adRequest += row.adRequest || 0;
        bucket.adReturn += row.adReturn || 0;
        bucket.impressions += row.impressions || 0;
        bucket.clicks += row.clicks || 0;
        return bucket;
    }

    finalizeMetricBucket = (bucket, metricKey) => {
        bucket.adReturnRate = bucket.adRequest > 0 ? (bucket.adReturn / bucket.adRequest) * 100 : 0;
        bucket.adFillRate = bucket.adRequest > 0 ? (bucket.impressions / bucket.adRequest) * 100 : 0;
        bucket.ctr = bucket.impressions > 0 ? (bucket.clicks / bucket.impressions) * 100 : 0;
        bucket.value = bucket[metricKey] || 0;
        return bucket;
    }

    isSuperAdmin = () => {
        const { userProfile } = this.props;
        return userProfile?.user?.role === 'superadmin';
    }

    getFilteredRows = () => {
        const { rawRows, selectedRegion, selectedAdFormat, dateRange } = this.state;
        let rows = rawRows;
        if (selectedRegion && selectedRegion !== 'all') {
            rows = rows.filter(r => (r.region || '').toLowerCase() === String(selectedRegion).toLowerCase());
        }
        if (selectedAdFormat && selectedAdFormat !== 'all') {
            rows = rows.filter(r => {
                const format = (r.adFormat || '').toLowerCase();
                return format.includes(this.normalizeAdFormat(selectedAdFormat));
            });
        }
        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
            const startDate = this.getDayBoundaryDate(dateRange[0], 'startOf');
            const endDate = this.getDayBoundaryDate(dateRange[1], 'endOf');
            if (!startDate || !endDate) {
                return rows;
            }
            const start = startDate.getTime();
            const end = endDate.getTime();
            rows = rows.filter(r => {
                const t = new Date(r.date).getTime();
                return t >= start && t <= end;
            });
        }
        return rows;
    }

    getRegionOptions = () => {
        const { rawRows } = this.state;
        const set = new Set();
        rawRows.forEach(r => {
            if (r.region && r.region !== '') set.add(r.region);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    normalizeAdFormat = (val) => {
        if (!val) return '';
        const normalized = String(val).toLowerCase();
        if (normalized === 'video') return 'video';
        return normalized;
    }

    computeAll = () => {
        const rows = this.getFilteredRows();
        const { selectedMetric } = this.state;

        const totals = rows.reduce((acc, r) => {
            acc.adRequest += r.adRequest || 0;
            acc.adReturn += r.adReturn || 0;
            acc.impressions += r.impressions || 0;
            acc.clicks += r.clicks || 0;
            if (r.region) acc.countries.add(r.region);
            return acc;
        }, { adRequest: 0, adReturn: 0, impressions: 0, clicks: 0, countries: new Set() });

        const averageCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
        const adReturnRate = totals.adRequest > 0 ? (totals.adReturn / totals.adRequest) * 100 : 0;
        const adFillRate = totals.adRequest > 0 ? (totals.impressions / totals.adRequest) * 100 : 0;
        const analytics = {
            totalAdRequest: totals.adRequest,
            totalAdReturn: totals.adReturn,
            adReturnRate,
            adFillRate,
            totalImpressions: totals.impressions,
            totalClicks: totals.clicks,
            averageCTR,
            totalCountries: totals.countries.size,
        };

        const byDateMap = new Map();
        rows.forEach(r => {
            const key = this.normalizeDateKey(r.date);
            if (!key) return;
            if (!byDateMap.has(key)) byDateMap.set(key, this.createMetricBucket({ date: key }));
            this.addRowToMetricBucket(byDateMap.get(key), r);
        });
        byDateMap.forEach(bucket => this.finalizeMetricBucket(bucket, selectedMetric));
        const metricData = this.buildContinuousMetricData(byDateMap, rows).map(bucket => this.finalizeMetricBucket(bucket, selectedMetric));

        const byRegion = new Map();
        rows.forEach(r => {
            const key = r.region || 'Unknown';
            if (!byRegion.has(key)) byRegion.set(key, this.createMetricBucket({ name: key, country: key }));
            this.addRowToMetricBucket(byRegion.get(key), r);
        });
        const geoData = Array.from(byRegion.values()).map(bucket => this.finalizeMetricBucket(bucket, selectedMetric));

        const byFormat = new Map();
        rows.forEach(r => {
            const key = r.adFormat || 'Unknown';
            if (!byFormat.has(key)) byFormat.set(key, this.createMetricBucket({ type: key }));
            this.addRowToMetricBucket(byFormat.get(key), r);
        });
        const adFormatData = Array.from(byFormat.values()).map(bucket => this.finalizeMetricBucket(bucket, selectedMetric));

        this.setState({
            dashboardAnalytics: analytics,
            dashboardMetricData: metricData,
            dashboardGeoData: geoData,
            dashboardAdFormatData: adFormatData,
            loadingAnalytics: false,
            loadingMetric: false,
            loadingGeo: false,
            loadingAdFormat: false,
        });
    }

    handleRegionChange = (value) => {
        this.setState({ selectedRegion: value }, this.computeAll);
    }

    handleBusinessUnitChange = (value) => {
        this.setState({ selectedBusinessUnit: value || null }, this.loadAdeventData);
    }

    handleAdFormatChange = (value) => {
        this.setState({ selectedAdFormat: value }, this.computeAll);
    }

    handleMetricChange = (value) => {
        this.setState({ selectedMetric: value }, this.computeAll);
    }

    handleDateRangeChange = (dates) => {
        this.setState({ dateRange: dates }, () => {
            this.loadAdeventData();
        });
    }

    renderKPICards = () => {
        const { dashboardAnalytics, loadingAnalytics } = this.state;
        const kpiData = dashboardAnalytics || {
            totalAdRequest: 0,
            totalAdReturn: 0,
            adReturnRate: 0,
            adFillRate: 0,
            totalImpressions: 0,
            totalClicks: 0,
            averageCTR: 0,
            totalCountries: 0,
        };

        const kpiCards = [
            {
                title: 'IMP',
                fullTitle: 'Total Impressions',
                value: kpiData.totalImpressions,
                precision: 0,
                suffix: '',
                shortSuffix: '',
                icon: <EyeOutlined />,
                color: '#722ed1',
                trendText: 'client impressions'
            },
            {
                title: 'CLICK',
                fullTitle: 'Total Clicks',
                value: kpiData.totalClicks,
                precision: 0,
                suffix: '',
                shortSuffix: '',
                icon: <ImportOutlined />,
                color: '#fa8c16',
                trendText: 'client clicks'
            },
            {
                title: 'CTR',
                fullTitle: 'Average CTR',
                value: kpiData.averageCTR,
                precision: 2,
                suffix: '%',
                shortSuffix: '%',
                icon: <PercentageOutlined />,
                color: '#eb2f96',
                trendText: 'click / imp'
            },
            {
                title: 'REG',
                fullTitle: 'Active Regions',
                value: kpiData.totalCountries,
                precision: 0,
                suffix: '',
                shortSuffix: '',
                icon: <GlobalOutlined />,
                color: '#2f54eb',
                trendText: 'new regions'
            },
            {
                title: 'Ad Request',
                fullTitle: 'Ad Requests Recorded by Pangle',
                value: kpiData.totalAdRequest,
                precision: 0,
                suffix: '',
                shortSuffix: '',
                icon: <ImportOutlined />,
                color: '#1890ff',
                trendText: ''
            },
            {
                title: 'Ad Return',
                fullTitle: 'Ads Successfully Assigned by Pangle',
                value: kpiData.totalAdReturn,
                precision: 0,
                suffix: '',
                shortSuffix: '',
                icon: <EyeOutlined />,
                color: '#13c2c2',
                trendText: ''
            },
            {
                title: 'Ad Return Rate',
                fullTitle: 'Ad Return / Ad Request',
                value: kpiData.adReturnRate,
                precision: 2,
                suffix: '%',
                shortSuffix: '%',
                icon: <PercentageOutlined />,
                color: '#52c41a',
                trendText: 'adReturn / adRequest'
            },
            {
                title: 'Ad Fill Rate',
                fullTitle: 'IMP / Ad Request',
                value: kpiData.adFillRate,
                precision: 2,
                suffix: '%',
                shortSuffix: '%',
                icon: <PercentageOutlined />,
                color: '#2f54eb',
                trendText: 'imp / adRequest'
            },
        ];

        if (loadingAnalytics) {
            return (
                <Row gutter={[16, 16]} className="kpi-section">
                    {kpiCards.map((_, index) => (
                        <Col xs={12} sm={8} md={6} lg={4} key={index}>
                            <div className="kpi-card loading">
                                <div className="kpi-skeleton">
                                    <div className="skeleton-icon"></div>
                                    <div className="skeleton-content">
                                        <div className="skeleton-title"></div>
                                        <div className="skeleton-value"></div>
                                        <div className="skeleton-trend"></div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            );
        }

        return (
            <Row gutter={[16, 16]} className="kpi-section">
                {kpiCards.map((card, index) => (
                    <Col xs={12} sm={8} md={6} lg={4} key={index}>
                        <div className="kpi-card">
                            <div className="kpi-header">
                                <div className="kpi-icon" style={{ color: card.color }}>
                                    {card.icon}
                                </div>
                                {/* <div className="kpi-trend">
                                    <RiseOutlined />
                                    <span>{card.trend}</span>
                                </div> */}
                            </div>

                            <div className="kpi-body">
                                <div className="kpi-title" title={card.fullTitle}>
                                    {card.title}
                                </div>
                                <div className="kpi-value">
                                    <span className="value-number">
                                        {this.formatKPIValue(card.value, card.precision)}
                                    </span>
                                    <span className="value-suffix">{card.shortSuffix}</span>
                                </div>
                            </div>

                            <div className="kpi-footer">
                                <span className="trend-text">{card.trendText}</span>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        );
    }

    formatKPIValue = (value, precision) => {
        if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
        } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'K';
        } else {
            return Number(value).toFixed(precision);
        }
    }

    formatMetricValue = (value, metric) => {
        const suffix = metric?.suffix || '';
        const precision = metric?.precision ?? 0;
        const numericValue = Number(value) || 0;
        return `${this.formatKPIValue(numericValue, precision)}${suffix}`;
    }

    renderMetricChart = () => {
        const { dashboardMetricData, loadingMetric, selectedMetric } = this.state;
        const metric = this.getMetricConfig(selectedMetric);
        const chartData = dashboardMetricData || [];

        const config = {
            data: chartData,
            xField: 'date',
            yField: 'value',
            smooth: true,
            height: 350,
            color: metric.color,
            point: {
                size: 4,
                shape: 'circle',
                style: {
                    fill: metric.color,
                    stroke: '#fff',
                    lineWidth: 2,
                },
            },
            area: {
                style: {
                    fill: 'l(270) 0:#ffffff 0.5:#7ec2f3 1:#1890ff',
                    fillOpacity: 0.3,
                },
            },
            yAxis: {
                label: {
                    formatter: (v) => this.formatMetricValue(v, metric),
                },
                grid: {
                    line: {
                        style: {
                            stroke: '#f0f0f0',
                        },
                    },
                },
            },
            xAxis: {
                grid: {
                    line: {
                        style: {
                            stroke: '#f0f0f0',
                        },
                    },
                },
            },
            tooltip: {
                showMarkers: true,
                title: (datum) => datum.date,
                items: [
                    (datum) => ({
                        name: metric.label,
                        value: this.formatMetricValue(datum.value || 0, metric),
                    }),
                ],
            },
        };

        return (
            <Card
                title={`${metric.label} Trend`}
                loading={loadingMetric}
                className="chart-card"
                extra={
                    <Select
                        value={selectedMetric}
                        style={{ width: 180 }}
                        onChange={this.handleMetricChange}
                        options={this.getMetricOptions().map(option => ({ value: option.key, label: option.label }))}
                    />
                }
            >
                <LinePlot {...config} />
            </Card>
        );
    }

    renderAdFormatDistribution = () => {
        const { dashboardAdFormatData, loadingAdFormat, selectedMetric } = this.state;
        const metric = this.getMetricConfig(selectedMetric);
        const adFormatData = dashboardAdFormatData || [];

        const pieData = adFormatData.map(item => ({
            type: item.type,
            value: item.value,
        }));
        const totalValue = pieData.reduce((sum, item) => sum + item.value, 0);

        const config = {
            data: pieData,
            angleField: 'value',
            colorField: 'type',
            radius: 0.8,
            height: 350,
            label: {
                position: 'outside',
                text: ({ type, value }) => {
                    const pct = totalValue ? (value / totalValue) * 100 : 0;
                    return `${type}: ${pct.toFixed(1)}%`;
                },
            },
            interactions: [
                {
                    type: 'element-selected',
                },
                {
                    type: 'element-active',
                },
            ],
            legend: {
                position: 'bottom',
            },
        };

        return (
            <Card title={`${metric.label} Distribution by Ad Format`} loading={loadingAdFormat} className="chart-card">
                <PiePlot {...config} />
            </Card>
        );
    }

    renderTopRegions = () => {
        const { dashboardGeoData, loadingGeo, selectedMetric } = this.state;
        const metric = this.getMetricConfig(selectedMetric);
        const geoData = (dashboardGeoData || []).slice().sort((a, b) => b.value - a.value).slice(0, 5);
        const maxValue = Math.max(...geoData.map(d => d.value), 0);

        const columns = [
            {
                title: 'Rank',
                key: 'rank',
                width: 60,
                render: (_, __, index) => (
                    <div className="rank-badge">
                        {index + 1}
                    </div>
                ),
            },
            {
                title: 'Region',
                dataIndex: 'name',
                key: 'name',
                render: (name) => (
                    <div className="region-info">
                        <GlobalOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        {name}
                    </div>
                ),
            },
            {
                title: metric.label,
                dataIndex: 'value',
                key: 'value',
                align: 'right',
                render: (value) => (
                    <div className="metric-cell">
                        <div className="metric-amount">{this.formatMetricValue(value || 0, metric)}</div>
                        <Progress
                            percent={maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0}
                            showInfo={false}
                            size="small"
                            strokeColor={metric.color}
                        />
                    </div>
                ),
            },
        ];

        return (
            <Card title="Top Performing Regions" loading={loadingGeo} className="chart-card">
                <Table
                    columns={columns}
                    dataSource={geoData}
                    rowKey={(r) => r.name}
                    pagination={false}
                    size="middle"
                    className="regions-table"
                />
            </Card>
        );
    }

    renderRecentActivities = () => {
        const { recentActivities } = this.state;

        return (
            <Card title="Recent Activities" className="chart-card">
                <List
                    itemLayout="horizontal"
                    dataSource={recentActivities}
                    renderItem={(item) => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={
                                    <Avatar
                                        style={{
                                            backgroundColor: this.getActivityColor(item.type),
                                            color: '#fff'
                                        }}
                                        icon={this.getActivityIcon(item.type)}
                                    />
                                }
                                title={
                                    <div className="activity-title">
                                        {item.title}
                                    </div>
                                }
                                description={
                                    <div className="activity-meta">
                                        <span className="activity-user">{item.user}</span>
                                        <span className="activity-time">{item.time}</span>
                                    </div>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Card>
        );
    }

    getActivityColor = (type) => {
        const colors = {
            campaign: '#1890ff',
            delivery: '#52c41a',
            expansion: '#722ed1',
            optimization: '#fa8c16',
        };
        return colors[type] || '#666';
    }

    getActivityIcon = (type) => {
        const icons = {
            campaign: <EyeOutlined />,
            delivery: <ImportOutlined />,
            expansion: <GlobalOutlined />,
            optimization: <RiseOutlined />,
        };
        return icons[type] || <UserOutlined />;
    }

    renderFilters = () => {
        const { selectedRegion, selectedAdFormat, selectedBusinessUnit, dateRange } = this.state;
        const regions = this.getRegionOptions();
        const businessUnitOptions = Array.isArray(businessUnit) ? businessUnit : [];

        return (
            <Card className="filter-card">
                <Row align="middle" justify="space-between">
                    <Col>
                        <div className="filter-title">Dashboard Filters</div>
                    </Col>
                    <Col>
                        <Space>
                            {businessUnitOptions.length > 0 && (
                                <Select
                                    value={selectedBusinessUnit || undefined}
                                    allowClear
                                    style={{ width: 200 }}
                                    onChange={this.handleBusinessUnitChange}
                                    placeholder="Select Business Unit"
                                    options={businessUnitOptions.map(unit => ({ value: unit, label: unit }))}
                                />
                            )}

                            <Select
                                value={selectedRegion}
                                style={{ width: 200 }}
                                onChange={this.handleRegionChange}
                                placeholder="Select Region"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={[{ value: 'all', label: "All Regions" }, ...regions.map(r => ({ value: r, label: r }))]}
                            >
                            </Select>

                            <Select
                                value={selectedAdFormat}
                                style={{ width: 180 }}
                                onChange={this.handleAdFormatChange}
                                placeholder="Select Ad Format"
                                options={[
                                    { value: 'all', label: 'All Ad Formats' },
                                    { value: 'banner', label: 'Banner' },
                                    { value: 'interstitial', label: 'Interstitial' },
                                    { value: 'instream_video', label: 'Instream Video' },
                                    { value: 'web_native', label: 'Web Native' },
                                    { value: 'app_native', label: 'App Native' },
                                    { value: 'native_3adbanner', label: 'native_3adbanner' },
                                    { value: 'app_open', label: 'App Open' },
                                    { value: 'rewarded', label: 'Rewarded' },
                                ]}
                            >
                            </Select>

                            <RangePicker
                                value={dateRange}
                                onChange={this.handleDateRangeChange}
                                placeholder={['Start Date', 'End Date']}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    }

    render() {
        return (
            <Animated animationIn="fadeIn">
                <div className="dashboard-wrapper" style={{ paddingBottom: (this.props.play) ? '100px' : '20px' }}>

                    {/* Header with Filters */}
                    <Row style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            {this.renderFilters()}
                        </Col>
                    </Row>

                    {/* KPI Cards */}
                    <Row style={{ marginBottom: 32 }}>
                        <Col span={24}>
                            {this.renderKPICards()}
                        </Col>
                    </Row>

                    {/* Main Charts Row */}
                    <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                        <Col xs={24} lg={16}>
                            {this.renderMetricChart()}
                        </Col>
                        <Col xs={24} lg={8}>
                            {this.renderAdFormatDistribution()}
                        </Col>
                    </Row>

                    {/* Secondary Charts Row */}
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={12}>
                            {this.renderTopRegions()}
                        </Col>
                        {/* <Col xs={24} lg={12}>
                            {this.renderRecentActivities()}
                        </Col> */}
                    </Row>

                </div>
            </Animated>
        );
    }
}

const mapStateToProps = state => ({
    router: state.router,
    userProfile: state.user.userProfile,
    selectedOrganization: state.user.selectedOrganization,
    env: state.user.env,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    adeventList,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
